import { useId } from "react";
import { Controller, type FieldValues } from "react-hook-form";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel
} from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import { useFieldControl, type BaseFieldProps } from "./fieldTypes";

type ToggleProps<T extends FieldValues> = Omit<BaseFieldProps<T>, "required">;

/** Checkbox bound to a boolean field; label to the right. */
export function CheckboxField<T extends FieldValues>({
  control,
  name,
  label,
  description,
  disabled,
  className,
  id: idProp
}: ToggleProps<T>) {
  const ctrl = useFieldControl(control);
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <Controller
      control={ctrl}
      name={name}
      render={({ field, fieldState }) => (
        <Field
          orientation="horizontal"
          className={cn("w-auto items-start gap-2", className)}
          data-disabled={disabled || undefined}
          data-invalid={fieldState.invalid || undefined}
        >
          <Checkbox
            id={id}
            ref={field.ref}
            name={field.name}
            checked={Boolean(field.value)}
            disabled={disabled}
            className="mt-0.5"
            aria-invalid={fieldState.invalid || undefined}
            onCheckedChange={(c) => field.onChange(c === true)}
            onBlur={field.onBlur}
          />
          <FieldContent>
            <FieldLabel htmlFor={id} className="font-normal">
              {label}
            </FieldLabel>
            {description ? (
              <FieldDescription className="text-xs">
                {description}
              </FieldDescription>
            ) : null}
            {fieldState.error ? (
              <FieldError errors={[fieldState.error]} />
            ) : null}
          </FieldContent>
        </Field>
      )}
    />
  );
}

/** Switch bound to a boolean field; label and description on the left. */
export function SwitchField<T extends FieldValues>({
  control,
  name,
  label,
  description,
  disabled,
  className,
  id: idProp
}: ToggleProps<T>) {
  const ctrl = useFieldControl(control);
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <Controller
      control={ctrl}
      name={name}
      render={({ field, fieldState }) => (
        <Field
          orientation="horizontal"
          className={cn("items-start justify-between gap-4", className)}
          data-disabled={disabled || undefined}
          data-invalid={fieldState.invalid || undefined}
        >
          <FieldContent>
            <FieldLabel htmlFor={id}>{label}</FieldLabel>
            {description ? (
              <FieldDescription className="text-xs">
                {description}
              </FieldDescription>
            ) : null}
            {fieldState.error ? (
              <FieldError errors={[fieldState.error]} />
            ) : null}
          </FieldContent>
          <Switch
            id={id}
            ref={field.ref}
            name={field.name}
            checked={Boolean(field.value)}
            disabled={disabled}
            onCheckedChange={field.onChange}
            onBlur={field.onBlur}
          />
        </Field>
      )}
    />
  );
}
